import { useMemo, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useToast } from "../../context/ToastContext";
import { type CatalogAdminView, type LadderStep, type PriceRuleView, type RuleDraft, type SimulateRow, defaultRuleDraft, draftOf, ruleBody, useCatalogMutation } from "../../hooks/catalog";
import { useLocale } from "../../hooks/useLocale";
import { api } from "../../lib/api";
import { localName, money } from "../../lib/format";
import { Empty, Pill, SectionHeader } from "../ui/Badges";
import { Modal } from "../ui/Modal";
import { IntInput, MoneyInput, Switch } from "./fields";

/** "first 60 min = 8.000 JOD, then every 60 min = 4.000 JOD": the ladder in one sentence. */
export function ladderSummary(steps: LadderStep[], t: (k: string, o?: Record<string, unknown>) => string, cur: string): string {
  return steps
    .map((s, i) => {
      const price = `${money(s.price)} ${cur}`;
      if (i === 0) return s.blocks === 1 ? t("pricing.sum.first", { min: s.blockMinutes, price }) : t("pricing.sum.firstN", { n: s.blocks ?? 1, min: s.blockMinutes, price });
      return s.blocks === null ? t("pricing.sum.then", { min: s.blockMinutes, price }) : t("pricing.sum.thenN", { n: s.blocks, min: s.blockMinutes, price });
    })
    .join(" · ");
}

const SIM_MINUTES = [30, 60, 61, 90, 120, 121, 180];
const ladderValid = (l: LadderStep[]) => l.length > 0 && l.every((s, i) => s.blockMinutes >= 1 && s.price >= 0 && (s.blocks === null ? i === l.length - 1 : s.blocks >= 1));

function RuleEditor({ initial, ruleId, data, onClose }: { initial: RuleDraft; ruleId: string | null; data: CatalogAdminView; onClose: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const [d, setD] = useState<RuleDraft>(initial);
  const set = <K extends keyof RuleDraft>(k: K, v: RuleDraft[K]) => setD((x) => ({ ...x, [k]: v }));
  const save = useCatalogMutation(async () => (ruleId ? api.put(`/catalog-admin/rules/${ruleId}`, ruleBody(d)) : api.post("/catalog-admin/rules", ruleBody(d))));
  const tickets = data.products.filter((p) => p.kind === "play_time");
  const last = d.ladder.at(-1);
  const boundedEnd = !!last && last.blocks !== null;
  const cur = t("currency");

  const setStep = (i: number, patch: Partial<LadderStep>) => setD((x) => ({ ...x, ladder: x.ladder.map((s, k) => (k === i ? { ...s, ...patch } : s)) }));
  const addStep = () => setD((x) => {
    const prev = x.ladder.at(-1)!;
    const ladder = x.ladder.map((s, k) => (k === x.ladder.length - 1 && s.blocks === null ? { ...s, blocks: 1 } : s)); // the old last step can no longer repeat forever
    return { ...x, ladder: [...ladder, { blockMinutes: prev.blockMinutes, price: prev.price, blocks: null }] };
  });
  const removeStep = (i: number) => setD((x) => {
    const ladder = x.ladder.filter((_, k) => k !== i);
    return { ...x, ladder };
  });

  const sim = useQuery({
    queryKey: ["catalog-sim", d.ladder, d.graceMinutes, d.dailyCap, d.overtimeBlockMinutes, d.overtimeBlockPrice],
    enabled: ladderValid(d.ladder),
    placeholderData: keepPreviousData,
    queryFn: () => api.post<{ rows: SimulateRow[] }>("/catalog-admin/simulate", {
      draft: { ladder: d.ladder, graceMinutes: d.graceMinutes, dailyCap: d.dailyCap, overtimeBlockMinutes: boundedEnd ? d.overtimeBlockMinutes : undefined, overtimeBlockPrice: boundedEnd ? d.overtimeBlockPrice : undefined },
      minutes: SIM_MINUTES,
    }),
  });

  const valid = d.name.trim().length > 0 && !!d.productId && ladderValid(d.ladder);
  async function submit() {
    try {
      await save.mutateAsync(undefined);
      toast(t("pricing.saved"), "success");
      onClose();
    } catch (e) {
      toastError(e);
    }
  }

  return (
    <Modal
      open
      size="lg"
      onClose={onClose}
      title={ruleId ? t("pricing.rule.editTitle") : t("pricing.rule.newTitle")}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>{t("common.cancel")}</button>
          <button type="button" className="btn-primary" disabled={!valid || save.isPending} onClick={() => void submit()}>{t("pricing.save")}</button>
        </>
      }
    >
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <label className="label" htmlFor="pr-product">{t("pricing.rule.ticket")}</label>
          <select id="pr-product" className="field" value={d.productId} disabled={!!ruleId} onChange={(e) => set("productId", e.target.value)}>
            {tickets.map((p) => <option key={p.id} value={p.id}>{localName(p, locale)}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="pr-name">{t("pricing.rule.name")}</label>
          <input id="pr-name" className="field" value={d.name} maxLength={80} onChange={(e) => set("name", e.target.value)} />
        </div>
        <div>
          <label className="label" htmlFor="pr-name-ar">{t("pricing.rule.nameAr")}</label>
          <input id="pr-name-ar" className="field" dir="rtl" value={d.nameAr} maxLength={80} onChange={(e) => set("nameAr", e.target.value)} />
        </div>
      </div>

      <fieldset className="space-y-2 rounded-2xl border border-purple-200 bg-purple-50/40 p-3">
        <legend className="px-1 text-[11px] font-black text-cosmic-purple">⏱️ {t("pricing.ladder.title")}</legend>
        <p className="text-[11px] font-semibold text-gray-600">{t("pricing.ladder.help")}</p>
        <div className="space-y-2" role="list">
          {d.ladder.map((s, i) => {
            const isLast = i === d.ladder.length - 1;
            return (
              <div key={i} role="listitem" className="grid grid-cols-2 items-end gap-2 rounded-xl border border-gray-200 bg-white p-2.5 sm:grid-cols-12" data-step={i + 1}>
                <div className="col-span-2 text-[11px] font-black text-cosmic-navy sm:col-span-2">{i === 0 ? t("pricing.ladder.first") : t("pricing.ladder.next", { n: i + 1 })}</div>
                <div className="sm:col-span-3">
                  <label className="label" htmlFor={`st-min-${i}`}>{t("pricing.ladder.blockMinutes")}</label>
                  <IntInput id={`st-min-${i}`} value={s.blockMinutes} min={1} max={1440} onChange={(n) => setStep(i, { blockMinutes: n })} />
                </div>
                <div className="sm:col-span-3">
                  <label className="label" htmlFor={`st-price-${i}`}>{t("pricing.ladder.price", { cur })}</label>
                  <MoneyInput id={`st-price-${i}`} value={s.price} onChange={(v) => setStep(i, { price: v ?? 0 })} />
                </div>
                <div className="sm:col-span-3">
                  <label className="label" htmlFor={`st-blocks-${i}`}>{t("pricing.ladder.blocks")}</label>
                  {s.blocks === null ? (
                    <div className="flex h-[38px] items-center text-xs font-black text-emerald-700">♾️ {t("pricing.ladder.repeats")}</div>
                  ) : (
                    <IntInput id={`st-blocks-${i}`} value={s.blocks} min={1} max={1000} onChange={(n) => setStep(i, { blocks: n })} />
                  )}
                  {isLast && <Switch checked={s.blocks === null} onChange={(on) => setStep(i, { blocks: on ? null : 1 })}>{t("pricing.ladder.repeatToggle")}</Switch>}
                </div>
                <div className="flex justify-end sm:col-span-1">
                  {d.ladder.length > 1 && <button type="button" className="btn-ghost !px-2 text-red-600" aria-label={t("pricing.ladder.remove", { n: i + 1 })} onClick={() => removeStep(i)}>🗑</button>}
                </div>
              </div>
            );
          })}
        </div>
        {d.ladder.length < 8 && <button type="button" className="btn-ghost text-xs" onClick={addStep}>＋ {t("pricing.ladder.add")}</button>}
        {ladderValid(d.ladder) && <p className="rounded-lg bg-white p-2 text-[11px] font-black text-cosmic-navy" data-testid="ladder-summary">{ladderSummary(d.ladder, t as never, cur)}</p>}
      </fieldset>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <label className="label" htmlFor="pr-grace">{t("pricing.rule.grace")}</label>
          <IntInput id="pr-grace" value={d.graceMinutes} min={0} max={240} onChange={(n) => set("graceMinutes", n)} />
          <p className="mt-0.5 text-[10px] font-semibold text-gray-500">{t("pricing.rule.graceHelp")}</p>
        </div>
        <div>
          <label className="label" htmlFor="pr-cap">{t("pricing.rule.cap", { cur })}</label>
          <MoneyInput id="pr-cap" value={d.dailyCap} allowEmpty placeholder={t("pricing.rule.noCap")} onChange={(v) => set("dailyCap", v)} />
          <p className="mt-0.5 text-[10px] font-semibold text-gray-500">{t("pricing.rule.capHelp")}</p>
        </div>
        <div>
          <label className="label" htmlFor="pr-age">{t("pricing.rule.age")}</label>
          <select id="pr-age" className="field" value={d.ageGroupId ?? ""} onChange={(e) => set("ageGroupId", e.target.value || null)}>
            <option value="">{t("pricing.rule.everyAge")}</option>
            {data.ageGroups.map((a) => <option key={a.id} value={a.id}>{(locale === "ar" && a.name_ar) || a.name}</option>)}
          </select>
        </div>
        <div>
          <label className="label" htmlFor="pr-priority">{t("pricing.rule.priority")}</label>
          <IntInput id="pr-priority" value={d.priority} min={-1000} max={1000} onChange={(n) => set("priority", n)} />
          <p className="mt-0.5 text-[10px] font-semibold text-gray-500">{t("pricing.rule.priorityHelp")}</p>
        </div>
      </div>

      {boundedEnd && (
        <div className="grid grid-cols-1 gap-3 rounded-xl border border-amber-200 bg-amber-50/50 p-3 sm:grid-cols-3">
          <p className="text-[11px] font-bold text-amber-900 sm:col-span-3">{t("pricing.rule.beyondHelp")}</p>
          <div>
            <label className="label" htmlFor="pr-ot-min">{t("pricing.rule.beyondMinutes")}</label>
            <IntInput id="pr-ot-min" value={d.overtimeBlockMinutes ?? last!.blockMinutes} min={1} max={1440} onChange={(n) => set("overtimeBlockMinutes", n)} />
          </div>
          <div>
            <label className="label" htmlFor="pr-ot-price">{t("pricing.rule.beyondPrice", { cur })}</label>
            <MoneyInput id="pr-ot-price" value={d.overtimeBlockPrice ?? last!.price} onChange={(v) => set("overtimeBlockPrice", v ?? 0)} />
          </div>
        </div>
      )}

      <fieldset className="space-y-2 rounded-2xl border border-emerald-200 bg-emerald-50/40 p-3">
        <legend className="px-1 text-[11px] font-black text-emerald-800">💬 {t("pricing.notify.title")}</legend>
        <Switch checked={d.notifyEnabled} onChange={(v) => set("notifyEnabled", v)}>{t("pricing.notify.enabled")}</Switch>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div>
            <label className="label" htmlFor="pr-notify">{t("pricing.notify.before")}</label>
            <IntInput id="pr-notify" value={d.notifyBeforeMinutes} min={1} max={720} disabled={!d.notifyEnabled} onChange={(n) => set("notifyBeforeMinutes", n)} />
          </div>
          <p className="self-end text-[11px] font-semibold text-gray-600 sm:col-span-2">
            {d.notifyEnabled && last ? t("pricing.notify.example", { before: d.notifyBeforeMinutes, at: Math.max(0, d.ladder[0]!.blockMinutes - d.notifyBeforeMinutes), block: d.ladder[0]!.blockMinutes }) : t("pricing.notify.off")}
          </p>
        </div>
      </fieldset>

      <div className="flex flex-wrap gap-x-6 gap-y-2">
        <Switch checked={d.taxInclusive} onChange={(v) => set("taxInclusive", v)}>{t("pricing.rule.taxInclusive")}</Switch>
        <Switch checked={d.thisBranchOnly} onChange={(v) => set("thisBranchOnly", v)}>{t("pricing.rule.branchOnly")}</Switch>
        <Switch checked={d.active} onChange={(v) => set("active", v)}>{t("pricing.rule.active")}</Switch>
      </div>

      <div className="rounded-2xl border border-gray-200 p-3" data-testid="simulator">
        <div className="mb-1.5 text-[11px] font-black text-gray-700">🧮 {t("pricing.sim.title")}</div>
        <p className="mb-2 text-[10px] font-semibold text-gray-500">{t("pricing.sim.help")}</p>
        <div className="grid grid-cols-2 gap-1.5 sm:grid-cols-7">
          {(sim.data?.rows ?? []).map((r) => (
            <div key={r.minutes} className="rounded-lg bg-gray-50 p-1.5 text-center">
              <div className="num text-[10px] font-bold text-gray-500">{r.minutes} {t("common.min")}</div>
              <div className="num font-mono text-xs font-black text-cosmic-purple">{money(r.price)}</div>
              <div className="text-[9px] font-semibold text-gray-400">{t("pricing.sim.blocks", { count: r.blocks })}{r.capped ? " ⛔" : ""}</div>
            </div>
          ))}
        </div>
      </div>
    </Modal>
  );
}

export function RulesPanel({ data, canEdit }: { data: CatalogAdminView; canEdit: boolean }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { toast, toastError } = useToast();
  const [editing, setEditing] = useState<{ id: string | null; draft: RuleDraft } | null>(null);
  const remove = useCatalogMutation((id: string) => api.delete<{ deactivatedOnly: boolean }>(`/catalog-admin/rules/${id}`));
  const tickets = data.products.filter((p) => p.kind === "play_time");
  const cur = t("currency");
  const byProduct = useMemo(() => tickets.map((p) => ({ product: p, rules: data.rules.filter((r) => r.productId === p.id) })), [tickets, data.rules]);
  const age = (r: PriceRuleView) => data.ageGroups.find((a) => a.id === r.ageGroupId);

  async function del(r: PriceRuleView) {
    if (!window.confirm(t("pricing.rule.confirmDelete", { name: r.name }))) return;
    try {
      const res = await remove.mutateAsync(r.id);
      toast(res.deactivatedOnly ? t("pricing.rule.deactivated") : t("pricing.rule.deleted"), "success");
    } catch (e) {
      toastError(e);
    }
  }

  if (tickets.length === 0) return <Empty>{t("pricing.rule.noTickets")}</Empty>;
  return (
    <div className="space-y-4">
      {byProduct.map(({ product, rules }) => (
        <section key={product.id} className="card space-y-3 p-4 sm:p-5" aria-label={product.name}>
          <SectionHeader
            title={`🎟️ ${localName(product, locale)}`}
            subtitle={t("pricing.rule.ticketHelp")}
            right={canEdit ? <button type="button" className="btn-primary !px-3 !py-1.5 text-xs" onClick={() => setEditing({ id: null, draft: defaultRuleDraft(product.id) })}>＋ {t("pricing.rule.add")}</button> : undefined}
          />
          {rules.length === 0 ? (
            <Empty>{t("pricing.rule.none")}</Empty>
          ) : (
            <div className="space-y-2">
              {rules.map((r) => (
                <div key={r.id} className={`rounded-2xl border p-3 ${r.active ? "border-gray-200 bg-gray-50" : "border-gray-200 bg-gray-100 opacity-70"}`} data-rule={r.name}>
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <div className="flex flex-wrap items-center gap-1.5 text-sm font-black text-cosmic-navy">
                        {(locale === "ar" && r.nameAr) || r.name}
                        {!r.active && <Pill tone="gray">{t("pricing.rule.inactive")}</Pill>}
                        {age(r) && <Pill tone="amber">{(locale === "ar" && age(r)!.name_ar) || age(r)!.name}</Pill>}
                        {r.branchId && <Pill tone="purple">{t("pricing.rule.thisBranch")}</Pill>}
                        {r.openVisits > 0 && <Pill tone="green">{t("pricing.rule.openVisits", { count: r.openVisits })}</Pill>}
                      </div>
                      <div className="mt-1 text-xs font-bold text-gray-700">{ladderSummary(r.ladder, t as never, cur)}</div>
                      <div className="mt-0.5 text-[11px] font-semibold text-gray-500">
                        {t("pricing.rule.graceShort", { min: r.graceMinutes })} · {r.dailyCap !== null ? t("pricing.rule.capShort", { amount: money(r.dailyCap), cur }) : t("pricing.rule.noCap")} ·{" "}
                        {r.notifyEnabled ? t("pricing.rule.notifyShort", { min: r.notifyBeforeMinutes }) : t("pricing.notify.off")} · {r.taxInclusive ? t("pricing.rule.taxIncl") : t("pricing.rule.taxExcl")}
                      </div>
                      {!r.ladderRegular && <p className="mt-1 text-[11px] font-bold text-amber-700">⚠️ {t("pricing.rule.irregular")}</p>}
                    </div>
                    {canEdit && (
                      <div className="flex gap-1.5">
                        <button type="button" className="btn-ghost !px-3 !py-1 text-xs" onClick={() => setEditing({ id: r.id, draft: draftOf(r) })}>✏️ {t("pricing.edit")}</button>
                        <button type="button" className="btn-ghost !px-2 !py-1 text-xs text-red-600" aria-label={t("pricing.rule.delete")} onClick={() => void del(r)}>🗑</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      ))}
      {editing && <RuleEditor initial={editing.draft} ruleId={editing.id} data={data} onClose={() => setEditing(null)} />}
    </div>
  );
}
